import React, { FC, useState } from 'react';
import classNames from 'classnames';
import { Popup } from 'shared/ui/Popup';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { WarehouseForBooking } from 'entities/Warehouse';
import { Trans } from 'react-i18next';
import { Image } from 'antd';
import { ReactComponent as ClockIcon } from 'shared/assets/icons/ClockIcon.svg';
import { ReactComponent as CameraIcon } from 'shared/assets/icons/CameraIcon.svg';
import { ReactComponent as TrolleyIcon } from 'shared/assets/icons/TrolleyIcon.svg';
import { ReactComponent as PaymentCardIcon } from 'shared/assets/icons/PaymentCardIcon.svg';
import { ReactComponent as OfficeIcon } from 'shared/assets/icons/OfficeIcon.svg';
import WarehouseDefaultImage from 'shared/assets/images/WarehouseDefaultImage.png';
import { Tag } from 'shared/ui/Tag';
import { getGlobalSettings, useGetCurrencySymbol } from 'app/appState';
import { ReactComponent as EmailIcon } from 'shared/assets/icons/EmailIcon.svg';
import { ReactComponent as PhoneIcon } from 'shared/assets/icons/PhoneIcon.svg';
import { ReactComponent as SmartphoneIcon } from 'shared/assets/icons/SmartphoneIcon.svg';
import { CopyTooltip } from 'shared/ui/CopyTooltip';
import { ReadMore } from 'shared/ui/ReadMore';
import { useGeolocation } from 'shared/utils/hooks/useGeolocation';
import { SizeGroupsTags } from 'entities/SizeCode';
import { Map } from 'features/Map';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { getBoxDiscountedPriceAmount, getBoxPriceAmountWithVat, useGetMinBoxPriceAmount } from 'entities/Box';
import { Button } from 'shared/ui/Button';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getLoggedUserData } from 'entities/User';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { WarnTag } from 'shared/ui/WarnTag';

interface WarehouseInfoStackProps {
  warehouseInfo: Nullable<WarehouseForBooking>;
}

export const WarehouseInfoStack: FC<WarehouseInfoStackProps> = (props) => {
  const { warehouseInfo } = props;

  const { t } = useAppTranslation('booking');
  const currencySymbol = useGetCurrencySymbol();
  const geolocationCoords = useGeolocation();
  const globalSettings = useAppSelector(getGlobalSettings);
  const loggedUser = useAppSelector(getLoggedUserData);

  const [isPreviewVisible, setIsPreviewVisible] = useState(false);

  const warehouseImage = warehouseInfo?.imageUrl || WarehouseDefaultImage;
  const fullAddress = `${warehouseInfo?.address || ''} ${warehouseInfo?.zip || ''}`;
  const discountedPrice = getBoxDiscountedPriceAmount({
    warehouseDiscounts: warehouseInfo?.warehouseDiscounts,
    boxPrice: warehouseInfo?.minPrice,
  });

  const minBoxPrice = useGetMinBoxPriceAmount(warehouseInfo);
  const discountedBoxPriceWithVat = getBoxPriceAmountWithVat(discountedPrice, warehouseInfo, loggedUser?.accountType);

  const discountedBoxPrice = roundNumber(globalSettings?.showPriceWithVat ? discountedBoxPriceWithVat : discountedPrice);

  const mapCenter = warehouseInfo ? { lat: warehouseInfo.latitude, lng: warehouseInfo.longitude } : geolocationCoords;
  const mapMarkers = warehouseInfo
    ? [{ id: warehouseInfo.warehouseId, position: { lat: warehouseInfo.latitude, lng: warehouseInfo.longitude } }]
    : undefined;

  return (
    <div>
      <div className="font-semibold text-2xl">{warehouseInfo?.name}</div>
      <CopyTooltip className="cursor-pointer text-primaryLight text-sm font-normal">{fullAddress}</CopyTooltip>
      {/* <img className="rounded-lg h-[139px] w-full object-cover my-4" src={warehouseImage} alt="Warehouse photo" /> */}

      <div className="my-1">
        <Image
          height={139}
          width="100%"
          className="rounded-lg object-cover"
          src={warehouseImage}
          preview={{
            visible: isPreviewVisible,
            scaleStep: 3,
            src: warehouseImage,
            onVisibleChange: (value) => {
              setIsPreviewVisible(value);
            },
          }}
        />
      </div>

      <Button
        containerClassName="items-end"
        theme="clear"
        onClick={() => {
          setIsPreviewVisible(true);
        }}
      >
        {t('Expand image')}
      </Button>

      <div className="text-2xl font-semibold mb-4">
        <Trans
          t={t}
          i18nKey={`${
            discountedBoxPrice ? 'From <0>{{discountedPrice}} {{currencySymbol}}/month</0>' : ''
          } <1>From {{minPrice}} {{currencySymbol}}/month</1>`}
          components={[
            <span key="0" className="text-accent text-xl font-semibold" />,
            <div key="1" className={discountedBoxPrice ? 'line-through text-error' : 'text-primary text-xl font-semibold'} />,
          ]}
          values={{ minPrice: minBoxPrice, currencySymbol, discountedPrice: discountedBoxPrice }}
        />
      </div>
      <div className="space-y-2.5 mb-4">
        <div className="flex items-center space-x-2.5">
          <EmailIcon className="shrink-0 [&>path]:stroke-accent" />
          <CopyTooltip className="cursor-pointer text font-normal">{warehouseInfo?.email}</CopyTooltip>
        </div>
        <div className="flex items-center space-x-2.5">
          <PhoneIcon className="shrink-0 [&>path]:stroke-accent" />
          <CopyTooltip className="cursor-pointer text font-normal">{warehouseInfo?.phone}</CopyTooltip>
        </div>
        {warehouseInfo?.warningInfo && (
          <div className="flex items-center space-x-2.5">
            <WarnTag textSize="small" className="font-semibold px-0" iconClassName="w-[23px] h-[23px]">
              <span className="font-semibold">{getLocalizedString(warehouseInfo.warningInfo)}</span>
            </WarnTag>
          </div>
        )}
      </div>
      <div className="text-success mb-7">
        {t('{{availableBoxesAmount}} boxes free', { availableBoxesAmount: warehouseInfo?.availableBoxesAmount })}
      </div>
      <div className="flex flex-row items-center">
        <SizeGroupsTags sizeCodes={warehouseInfo?.sizeCodes} />
        <div>
          {warehouseInfo?.hasOffices && (
            <Popup
              title={t('Office rent')}
              description={t('On this location you can rent office')}
              color="var(--accent)"
              textColor="var(--white)"
            >
              <div
                className={classNames(
                  'flex justify-center items-center text-white text-sm font-normal bg-accent rounded-lg w-20 h-8 cursor-pointer mb-[5px] ml-5',
                )}
              >
                {t('Offices')}
              </div>
            </Popup>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 my-7">
        <Tag icon={<CameraIcon className="[&>path]:fill-accent" />}>{t('Security cameras monitoring')}</Tag>
        {!warehouseInfo?.workingSchedule.length && (
          <Tag icon={<ClockIcon className="[&>*]:stroke-accent [&>*]:stroke-1" />}>{t('24 / 7 direct access')}</Tag>
        )}
        <Tag icon={<TrolleyIcon className="[&>path]:fill-accent" />}>{t('Free use of trolleys')}</Tag>
        <Tag icon={<SmartphoneIcon className="[&>path]:stroke-accent" />}>{t('Opened by app')}</Tag>
        <Tag icon={<PaymentCardIcon className="stroke-accent" />}>{t('Online payments')}</Tag>
        {warehouseInfo?.hasOffices && <Tag icon={<OfficeIcon className="[&>path]:stroke-accent" />}>{t('Office rent')}</Tag>}
      </div>
      <ReadMore html={getLocalizedString(warehouseInfo?.description)} rows={3} />
      <Map
        mapContainerClassName="h-[280px]"
        mapId="warehouseMap"
        center={mapCenter}
        withMarkerPopups={false}
        markers={mapMarkers}
        zoom={14}
      />
    </div>
  );
};
